import { GateMovementState2 as State } from '@/__generated__/graphql';
import { useFormatters } from '@/hooks/useFormatters';
import TimeElapsed from '@components/TimeElapsed';
import { MovementDataRenderer } from '@features/gateCommon';
import { formatPosition } from '@features/gateCommon/utils/formatPosition';
import { getStateColor } from '@features/gateCommon/utils/gateMovementState';
import { t, Trans } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { Skeleton } from '@mui/material';
import { FC, ReactNode } from 'react';

interface Props {
  movementId: number | null;
  render: FC<{
    label: string;
    position: ReactNode;
    relativeTime: ReactNode;
    color: string;
    contrastColor: string;
  }>;
}

const defaultColors = getStateColor(State.InQueue);

export const OperationPositionData: FC<Props> = ({ movementId, render }) => {
  const { formatMetricValue } = useFormatters();
  const { i18n } = useLingui();

  return movementId ? (
    <MovementDataRenderer
      movementId={movementId}
      render={({ movement }) => {
        const finalizedStates = [State.Finalized, State.FinalizedWithError];

        let label = t(i18n)`Posición actual`;
        let position: number | null = movement.lastPosition;
        let timestamp: number | null = movement.lastPositionTimestamp;

        if (finalizedStates.includes(movement.state)) {
          label = t(i18n)`Posición final`;
          position = movement.finishPosition ?? null;
          timestamp = movement.finishTimestamp ?? null;
        }

        const { color, contrastColor } = getStateColor(movement.state);

        return render({
          label,
          position:
            position !== null ? (
              formatPosition(position, formatMetricValue)
            ) : (
              <Trans>Sin datos</Trans>
            ),
          relativeTime: timestamp ? (
            <TimeElapsed timestamp={timestamp} />
          ) : (
            <Trans>Sin datos</Trans>
          ),
          color,
          contrastColor,
        });
      }}
      fallback={() =>
        render({
          label: t(i18n)`Posición actual`,
          position: <Skeleton>xx cm</Skeleton>,
          relativeTime: <Skeleton>hace x minutos</Skeleton>,
          color: defaultColors.color,
          contrastColor: defaultColors.contrastColor,
        })
      }
    />
  ) : (
    <Trans>Iniciando comunicación</Trans>
  );
};
